import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ShoppingCart, Plus, Minus, Trash2, Search, Package,
  Truck, Clock, CreditCard, CheckCircle, Sparkles,
  ExternalLink, FileText, Download
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import api from '@/services/api';
import { toast } from 'sonner';
import InfoTip from '@/components/InfoTip';
import { useBranding } from '@/context/BrandingContext';
import { createBrandedPDF, addSectionTitle, addMetricCards, addTable, downloadPDF } from '@/utils/pdfExport';

const QuickOrder = () => {
  const { branding } = useBranding();
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([
    { id: 1, mpn: 'STM32F407VGT6', manufacturer: 'STMicroelectronics', description: 'ARM Cortex-M4 MCU, 1MB Flash', quantity: 100, unitPrice: 12.50, distributor: 'DigiKey', leadTime: '2 weeks', inStock: true },
    { id: 2, mpn: 'GRM188R71H104KA93D', manufacturer: 'Murata', description: '0.1uF 50V X7R 0603', quantity: 10000, unitPrice: 0.025, distributor: 'Mouser', leadTime: 'In Stock', inStock: true },
    { id: 3, mpn: 'ESP32-WROOM-32E', manufacturer: 'Espressif', description: 'WiFi+BT Module, 4MB Flash', quantity: 250, unitPrice: 2.80, distributor: 'Arrow', leadTime: '1 week', inStock: true },
  ]);

  // PO Generation state
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedPO, setGeneratedPO] = useState(null);
  const [poTerms, setPoTerms] = useState({
    payment_terms: 'Net 30',
    delivery_terms: 'FOB Destination',
    incoterms: 'DDP',
    shipping_method: 'standard',
    shipping_address: '',
    notes: '',
  });

  const updateQuantity = (id, delta) => {
    setCart(cart.map(item => item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item));
  };
  const removeItem = (id) => setCart(cart.filter(item => item.id !== id));

  const subtotal = cart.reduce((s, i) => s + i.quantity * i.unitPrice, 0);
  const shipping = 45.0;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  const generatePO = async () => {
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/orders/generate-po', {
        items: cart.map(i => ({ mpn: i.mpn, manufacturer: i.manufacturer, quantity: i.quantity, unit_price: i.unitPrice, distributor: i.distributor })),
        ...poTerms,
        tax_rate: 0.08,
        shipping: shipping,
      });
      setGeneratedPO(res.data);
      toast.success(`PO ${res.data.po_number} generated!`);
    } catch { toast.error('Failed to generate PO'); }
    finally { setGenerating(false); }
  };

  const downloadPOPdf = () => {
    if (!generatedPO) return;
    const doc = createBrandedPDF(`Purchase Order ${generatedPO.po_number}`, 'Auto-Generated Purchase Order', branding);
    let y = doc._startY;
    y = addSectionTitle(doc, 'PO Details', y);
    y = addMetricCards(doc, [
      { label: 'PO Number', value: generatedPO.po_number },
      { label: 'Total', value: `$${generatedPO.total?.toFixed(2)}` },
      { label: 'Payment', value: poTerms.payment_terms },
      { label: 'Incoterms', value: poTerms.incoterms },
    ], y);
    y = addSectionTitle(doc, 'Line Items', y);
    y = addTable(doc,
      ['MPN', 'Manufacturer', 'Qty', 'Unit Price', 'Total', 'Distributor'],
      cart.map(i => [i.mpn, i.manufacturer, String(i.quantity), `$${i.unitPrice.toFixed(3)}`, `$${(i.quantity * i.unitPrice).toFixed(2)}`, i.distributor]),
      y
    );
    downloadPDF(doc, `${generatedPO.po_number}.pdf`);
    toast.success('PO PDF downloaded');
  };

  return (
    <Layout title="1Spot Orders" subtitle="Instant spot procurement — search, compare terms, and order directly">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="quick-order-page">
        <div className="lg:col-span-2 space-y-6">
          {/* Search */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input placeholder="Search by MPN, description, or manufacturer..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-components" />
                </div>
                <Button className="bg-green-600 hover:bg-green-700 text-white"><Sparkles className="h-4 w-4 mr-2" /> AI Search</Button>
              </div>
            </CardContent>
          </Card>

          {/* Cart */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-gray-900 flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Order Cart ({cart.length} items)</CardTitle>
                <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setCart([])}><Trash2 className="h-4 w-4 mr-2" /> Clear All</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {cart.map(item => (
                  <div key={item.id} className="p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-all" data-testid={`cart-item-${item.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-gray-900">{item.mpn}</h3>
                          {item.inStock && <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" /> In Stock</Badge>}
                        </div>
                        <p className="text-sm text-gray-500">{item.manufacturer}</p>
                        <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                        <div className="flex items-center gap-4 mt-3 text-sm">
                          <span className="text-gray-500"><Truck className="h-4 w-4 inline mr-1" />{item.leadTime}</span>
                          <span className="text-gray-500"><Package className="h-4 w-4 inline mr-1" />{item.distributor}</span>
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-lg font-bold text-gray-900">${(item.quantity * item.unitPrice).toFixed(2)}</p>
                        <p className="text-sm text-gray-500">${item.unitPrice.toFixed(3)}/ea</p>
                        <div className="flex items-center gap-2 mt-3">
                          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => updateQuantity(item.id, -100)}><Minus className="h-4 w-4" /></Button>
                          <Input type="number" value={item.quantity} onChange={e => setCart(cart.map(i => i.id === item.id ? { ...i, quantity: parseInt(e.target.value) || 1 } : i))} className="w-20 h-8 text-center" />
                          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => updateQuantity(item.id, 100)}><Plus className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => removeItem(item.id)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" className="w-full mt-4 border-dashed border-2"><Plus className="h-4 w-4 mr-2" /> Add More Components</Button>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="bg-white border-gray-200 shadow-sm sticky top-6">
            <CardHeader><CardTitle className="text-gray-900">Order Summary</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal ({cart.length} items)</span><span className="font-medium">${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Shipping</span><span className="font-medium">${shipping.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Tax (8%)</span><span className="font-medium">${tax.toFixed(2)}</span></div>
                <div className="border-t pt-4"><div className="flex justify-between"><span className="text-lg font-bold">Total</span><span className="text-lg font-bold text-green-600">${total.toFixed(2)}</span></div></div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-green-800">AI Savings Found!</p>
                      <p className="text-xs text-green-700 mt-1">Smart alternates could save <strong>$245.00</strong>.</p>
                    </div>
                  </div>
                </div>

                {/* Generate PO Button */}
                <Button onClick={() => setPoDialogOpen(true)} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-lg" data-testid="generate-po-btn">
                  <FileText className="h-5 w-5 mr-2" /> Generate Purchase Order
                </Button>

                <Button className="w-full bg-green-600 hover:bg-green-700 text-white h-12" data-testid="checkout-btn"
                  onClick={() => toast.success(`Checkout initiated for ${cart.length} items — total $${total.toFixed(2)}`)}>
                  <CreditCard className="h-5 w-5 mr-2" /> Proceed to Checkout
                </Button>
                <Button variant="outline" className="w-full" data-testid="request-quote-btn"
                  onClick={() => toast.success(`RFQ sent for ${cart.length} items to connected distributors`)}>
                  <ExternalLink className="h-4 w-4 mr-2" /> Request Quote
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-50 border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <h4 className="font-medium text-gray-900 mb-3">Connected Distributors</h4>
              <div className="space-y-2">
                {['DigiKey', 'Mouser', 'Arrow', 'Farnell'].map((d, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{d}</span>
                    <Badge className="bg-green-100 text-green-800 text-xs"><CheckCircle className="h-3 w-3 mr-1" /> Connected</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* PO Generation Dialog */}
      <Dialog open={poDialogOpen} onOpenChange={setPoDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" /> Generate Purchase Order</DialogTitle>
          </DialogHeader>
          {!generatedPO ? (
            <div className="space-y-4 py-2" data-testid="po-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Terms <InfoTip text="Standard payment terms for this PO" /></Label>
                  <Select value={poTerms.payment_terms} onValueChange={v => setPoTerms({ ...poTerms, payment_terms: v })}>
                    <SelectTrigger className="mt-1" data-testid="po-payment-terms"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Net 15">Net 15</SelectItem>
                      <SelectItem value="Net 30">Net 30</SelectItem>
                      <SelectItem value="Net 45">Net 45</SelectItem>
                      <SelectItem value="Net 60">Net 60</SelectItem>
                      <SelectItem value="COD">Cash on Delivery</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Incoterms</Label>
                  <Select value={poTerms.incoterms} onValueChange={v => setPoTerms({ ...poTerms, incoterms: v })}>
                    <SelectTrigger className="mt-1" data-testid="po-incoterms"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EXW">EXW</SelectItem>
                      <SelectItem value="FOB">FOB</SelectItem>
                      <SelectItem value="CIF">CIF</SelectItem>
                      <SelectItem value="DAP">DAP</SelectItem>
                      <SelectItem value="DDP">DDP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Delivery Terms</Label>
                <Select value={poTerms.delivery_terms} onValueChange={v => setPoTerms({ ...poTerms, delivery_terms: v })}>
                  <SelectTrigger className="mt-1" data-testid="po-delivery-terms"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FOB Destination">FOB Destination</SelectItem>
                    <SelectItem value="FOB Origin">FOB Origin</SelectItem>
                    <SelectItem value="Ex Works">Ex Works</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Shipping Address</Label>
                <Input value={poTerms.shipping_address} onChange={e => setPoTerms({ ...poTerms, shipping_address: e.target.value })} placeholder="Enter shipping address..." className="mt-1" data-testid="po-shipping-address" />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea value={poTerms.notes} onChange={e => setPoTerms({ ...poTerms, notes: e.target.value })} placeholder="Special instructions..." className="mt-1" rows={2} data-testid="po-notes" />
              </div>
              <div className="bg-gray-50 rounded p-3 text-sm">
                <p className="font-medium text-gray-900">Order Summary</p>
                <p className="text-gray-600 mt-1">{cart.length} items | Total: <strong className="text-green-600">${total.toFixed(2)}</strong></p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2 text-center" data-testid="po-generated">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
              <h3 className="text-xl font-bold text-gray-900">PO Generated!</h3>
              <p className="text-lg font-mono text-green-700 break-all px-4">{generatedPO.po_number}</p>
              <p className="text-gray-600">Total: <strong>${generatedPO.total?.toFixed(2)}</strong></p>
              <Button onClick={downloadPOPdf} className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="download-po-pdf">
                <Download className="h-4 w-4 mr-2" /> Download PO PDF
              </Button>
            </div>
          )}
          <DialogFooter>
            {!generatedPO ? (
              <>
                <Button variant="outline" onClick={() => setPoDialogOpen(false)}>Cancel</Button>
                <Button onClick={generatePO} disabled={generating} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-po-btn">
                  {generating ? 'Generating...' : 'Generate PO'}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => { setPoDialogOpen(false); setGeneratedPO(null); }}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default QuickOrder;
